import { apiClient as api } from './apiClient';

// ═══════════════════════════════════════════════════════════════
// Nuevo panel de vinculación CU2 ↔ Gescom-AQ (sección "Vinculación CU2")
// Propiedades en camelCase (serialización .NET)
// ═══════════════════════════════════════════════════════════════

/** Pestaña 1 — Vinculación: métricas resumen. */
export interface PanelVinculacionResumen {
  // Farmacias CU2
  farmaciasCU2Total: number;
  farmaciasCU2Vinc: number;
  farmaciasCU2ConTitular: number;
  farmaciasCU2ConTitularVinc: number;
  // Farmacias Gescom-AQ
  farmaciasGSTotal: number;
  farmaciasGSVinc: number;
  // Farmacéuticos CU2
  colegiadosCU2Total: number;
  colegiadosCU2Vinc: number;
  colegiadosCU2Titulares: number;
  colegiadosCU2TitularesVinc: number;
  colegiadosCU2Empleados: number;
  colegiadosCU2EmpleadosVinc: number;
  // Farmacéuticos Gescom-AQ
  clientesGSTitulares: number;
  clientesGSTitularesVinc: number;
  clientesGSEmpleados: number;
  clientesGSEmpleadosVinc: number;
  clientesGSSinRelacionVinc: number;
}

/** GET /vinculacion/resumen */
export const getPanelVinculacionResumen = async (): Promise<PanelVinculacionResumen> => {
  const { data } = await api.get<PanelVinculacionResumen>('/vinculacion/resumen');
  return data;
};

// ── Pestaña 2 — Incoherencias (INC-01 … INC-06) ─────────────────────

export interface Inc01Row {
  id: number;
  nombre?: string;
  familyClass: number;
  fBaja?: string;
  nombreColegiadoCU2?: string;
  idColegiadoCU2?: number;
  esTitular: boolean;
  esEmpleado: boolean;
}
export interface Inc02Row {
  id: number;
  nombre?: string;
  idColegiadoCU2?: number;
  farmaciaTitular?: number;
  farmaciaEmpleado?: number;
}
export interface Inc04Row {
  idColegiado: number;
  nombreColegiado?: string;
  idColegiadoGs: number;
  propietario: boolean;
  numColegiado?: string;
  colegio?: string;
  nombreGescom?: string;
}
export interface Inc05Row {
  idEntidad: number;
  nombreGescom?: string;
  provincia?: string;
  direccion?: string;
  poblacion?: string;
  idFarmaciaCU2?: number;
  cu2Direccion?: string;
  cu2Localidad?: string;
  cu2NombreFarmacia?: string;
  cu2SinTitular: boolean;
}
export interface Inc06Row {
  idFarmacia: number;
  nombreFarmacia?: string;
  provincia?: string;
  direccion?: string;
  localidad?: string;
  idFarmaciaGs?: number;
  nombreGescom?: string;
}
export type BiunivocidadSubtipo = 'SIN_BACKLINK' | 'OTRO_CU2' | 'ID_INVALIDO';
export interface Inc07Row {
  idColegiado: number;
  nombreColegiado?: string;
  idColegiadoGs: number;
  propietario: boolean;
  numColegiado?: string;
  colegio?: string;
  subtipo: BiunivocidadSubtipo;
  backlinkActual?: number;
  nombreGescom?: string;
  confianza?: 'SEGURO' | 'DUDOSO' | 'ERROR';
  score?: number;
  dobleColegiacion: boolean;
}
export interface Inc08Row {
  idFarmacia: number;
  nombreFarmacia?: string;
  idFarmaciaGs: number;
  subtipo: BiunivocidadSubtipo;
  backlinkActual?: number;
  nombreGescom?: string;
}
export interface Inc09Row {
  idEntidad: number;
  nombre?: string;
  familyClass: number;
  fBaja?: string;
  idFarmaciaCU2?: number;
  tieneTitularActivo: boolean;
}
export interface PanelIncoherencias {
  inc01: Inc01Row[];
  inc02: Inc02Row[];
  inc04: Inc04Row[];
  inc05: Inc05Row[];
  inc06: Inc06Row[];
  inc07: Inc07Row[];
  inc08: Inc08Row[];
  inc09: Inc09Row[];
}

/** GET /vinculacion/incoherencias */
export const getPanelVinculacionIncoherencias = async (): Promise<PanelIncoherencias> => {
  const { data } = await api.get<PanelIncoherencias>('/vinculacion/incoherencias');
  return data;
};

// ── Pestaña 3 — Farmacias Gescom-AQ sin vincular ────────────────────

export interface TitularBreve {
  id: number;
  nombre?: string;
}
export interface FarmaciaSinVincular {
  id: number;
  farmacia?: string;
  poblacion?: string;
  provincia?: string;
  titulares: TitularBreve[];
}

/** GET /vinculacion/farmacias-sin-vincular */
export const getPanelFarmaciasSinVincular = async (): Promise<FarmaciaSinVincular[]> => {
  const { data } = await api.get<FarmaciaSinVincular[]>('/vinculacion/farmacias-sin-vincular');
  return data;
};

// ── Pestaña 4 — Titulares Gescom-AQ sin vincular ────────────────────

export type TitularSinVincularCategoria =
  | 'DUPLICADO_GS'
  | 'VINCULABLE'
  | 'TITULAR_DISTINTO'
  | 'TITULAR_CU2_NO_EN_GS'
  | 'CU2_SIN_TITULAR'
  | 'SIN_FARMACIA_CU2';

export interface CotitularCu2 {
  nombre?: string;
  idColegiado?: number;
  idColegiadoGs?: number;
  titGsEsta: boolean;
  titGsOtra: boolean;
}
export interface TitularSinVincular {
  categoria: TitularSinVincularCategoria;
  id: number;
  provincia?: string;
  titularGs?: string;
  farmaciaIdGs?: number;
  farmaciaGs?: string;
  direccionCu2?: string;
  localidadCu2?: string;
  nombreFarmaciaCu2?: string;
  idFarmaciaCu2?: number;
  titularesCu2: CotitularCu2[];
}

/** GET /vinculacion/titulares-sin-vincular */
export const getPanelTitularesSinVincular = async (): Promise<TitularSinVincular[]> => {
  const { data } = await api.get<TitularSinVincular[]>('/vinculacion/titulares-sin-vincular');
  return data;
};

// ── Pestaña 5 — Farmacias CU2 sin vincular ──────────────────────────

export interface FarmaciaCU2SinVincular {
  idFarmacia: number;
  nombreFarmacia?: string;
  direccion?: string;
  cp?: string;
  localidad?: string;
  provincia?: string;
  tieneTitular: boolean;
}

/** GET /vinculacion/farmacias-cu2-sin-vincular */
export const getPanelFarmaciasCU2SinVincular = async (): Promise<FarmaciaCU2SinVincular[]> => {
  const { data } = await api.get<FarmaciaCU2SinVincular[]>('/vinculacion/farmacias-cu2-sin-vincular');
  return data;
};

// ── Pestaña 6 — Farmacias duplicadas en CU2 ────────────────────────

export interface FarmaciaDuplicadaCU2 {
  idFarmacia: number;
  idFarmaciaGs?: number;
  nombreFarmacia?: string;
  direccion?: string;
  cp?: string;
  localidad?: string;
  provincia?: string;
}

/** GET /vinculacion/farmacias-duplicadas-cu2 */
export const getPanelFarmaciasDuplicadasCU2 = async (): Promise<FarmaciaDuplicadaCU2[]> => {
  const { data } = await api.get<FarmaciaDuplicadaCU2[]>('/vinculacion/farmacias-duplicadas-cu2');
  return data;
};

// ── Enlaces al CRM Gescom-AQ (gedeco) ───────────────────────────────

export const crmClienteUrl = (idAq: number | string): string =>
  `https://gedeco.farmaconsulting.es/FamiliasDefinibles_Ficha.aspx?id=${idAq}&idFam=25&idFamCls=59&pag=242&sraccess=1&tmp=758`;

export const crmFarmaciaUrl = (idFarmacia: number | string): string =>
  `https://gedeco.farmaconsulting.es/FamiliasDefinibles_Ficha.aspx?id=${idFarmacia}&idFam=3&idFamCls=15&pag=25&sraccess=1&tmp=616`;
